import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';

import { AuthFormIntro } from '@/components/auth/shell/auth-form-intro';
import { AuthSplitShell } from '@/components/auth/shell/auth-split-shell';
import { PlanPicker } from '@/components/billing/plan-picker';
import { Button } from '@/components/ui/button';
import { meQueryOptions } from '@/hooks/data/use-auth';
import { useBillingSummary } from '@/hooks/data/use-billing';
import { markPaywallSeen } from '@/lib/paywall-seen';
import { isInAgencyScope } from '@/lib/roles';
import { homePathFor } from '@/lib/routes';
import { ReadOnlyReason } from '@/types/generated/enums';
import { loadNamespaces } from '@/lib/i18n/i18n';

export const Route = createFileRoute('/_authenticated/paywall')({
  loader: () => loadNamespaces('auth'),
  staticData: { hideAppShell: true, title: 'route.paywall.title' },
  component: PaywallRoute,
});

// The ticket frames this as a two-way split (trial vs. subscription); the
// remaining ReadOnlyReason values fold into the "subscription" branch since
// no distinct copy was specified for them.
const HEADLINE_BY_REASON_KEYS: Partial<
  Record<ReadOnlyReason, ParseKeys<'billing'>>
> = {
  TRIAL_ENDED: 'paywall.headline_trial_ended',
  AGENCY_RELEASED: 'paywall.headline_agency_released',
  ADMIN: 'paywall.headline_admin',
};
const DEFAULT_HEADLINE_KEY: ParseKeys<'billing'> = 'paywall.headline_default';

function PaywallRoute(): React.ReactElement {
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const { data: user } = useQuery(meQueryOptions);
  const { data: summary, isLoading } = useBillingSummary();

  const continueReadOnly = (): void => {
    markPaywallSeen(user?.organization_id);
    void navigate({ to: homePathFor(user) });
  };

  const headline = t(
    (summary?.read_only_reason &&
      HEADLINE_BY_REASON_KEYS[summary.read_only_reason]) ??
      DEFAULT_HEADLINE_KEY,
  );
  // No payment lifts an operator's pause, so the page offers no plan.
  const pausedByOperator = summary?.read_only_reason === ReadOnlyReason.ADMIN;
  const agency = isInAgencyScope(user);
  const descriptionKey: ParseKeys<'billing'> = pausedByOperator
    ? agency
      ? 'paywall.form_description_admin_agency'
      : 'paywall.form_description_admin'
    : agency
      ? 'paywall.form_description_agency'
      : 'paywall.form_description';

  // The "Continue in read-only mode" escape must always render, even if the
  // summary fetch fails — this is the one route a read-only admin can't
  // afford to get stuck on with no way out.
  return (
    <AuthSplitShell>
      <AuthFormIntro title={headline} description={t(descriptionKey)} />

      {isLoading && (
        <p className="text-fg-3 text-fs-13">{t('paywall.loading_plans')}</p>
      )}
      {/* A failed refetch on tab focus keeps the plans it already had. */}
      {!isLoading && !summary && (
        <p className="text-fg-3 text-fs-13">{t('paywall.load_error')}</p>
      )}
      {summary && !pausedByOperator && <PlanPicker summary={summary} />}

      <Button variant="ghost" onClick={continueReadOnly}>
        {t('paywall.continue_read_only')}
      </Button>
    </AuthSplitShell>
  );
}
